<template>
  <article ref="target" class="flex flex-col gap-3 bg-white p-5 rounded-xl">
    <div class="grid grid-cols-1 lg:grid-cols-10 gap-5">
      <div v-if="props.newsItem.preview_img" class="lg:col-span-4">
        <BaseLocaleLink :to="route">
          <BaseRatio :ratio="9 / 21" size="cover">
            <div
              class="w-full h-full rounded-xl overflow-hidden flex items-center justify-center"
            >
              <BaseImage
                loading="lazy"
                reserved-height
                :src="props.newsItem.preview_img"
                class="w-full"
              />
            </div>
          </BaseRatio>
        </BaseLocaleLink>
      </div>
      <div class="lg:col-span-6">
        <div class="flex flex-col gap-3">
          <div
            class="relative flex justify-between flex-wrap gap-3 border-b-2 border-primary-100 pb-3"
          >
            <div class="absolute top-0 right-0">
              <CardOptionsMenu class="ml-auto" :page-link="newsPageLink" />
            </div>
            <div
              v-if="categories.length || newsItem.premium"
              class="flex flex-wrap gap-1 w-11/12"
            >
              <BaseLocaleLink
                v-if="newsItem.premium"
                :to="{ name: 'news-premium' }"
                class="flex no-underline font-normal"
              >
                <BaseButton
                  size="xs"
                  look="none"
                  class="bg-pro hover:bg-pro-800 text-white"
                >
                  <BaseIcon
                    name="solid_lightning_bolt"
                    size="xs"
                    class="mr-px"
                  />
                  {{ $t('common.premium') }}
                </BaseButton>
              </BaseLocaleLink>
              <BaseLocaleLink
                v-for="category in categories.slice(0, 3)"
                :key="category.id"
                :to="category.route"
                class="flex no-underline font-normal"
                :class="category.hidden && 'pointer-events-none'"
              >
                <BaseButton
                  size="xs"
                  look="none"
                  class="bg-primary-100 hover:bg-primary-400"
                >
                  {{ category.local }}
                </BaseButton>
              </BaseLocaleLink>
            </div>
            <div>
              <BaseLink theme="dark" class="!inline" :to="route">
                <h2 class="text-lg inline font-bold lg:text-xl">
                  {{ newsItem.name }}
                </h2>
              </BaseLink>
              <span class="text-lg text-gray-500 whitespace-nowrap">
                · {{ formatDateFrom(newsItem.publish_date) }}
              </span>
            </div>
          </div>
          <div
            class="text-gray text-sm line-clamp-4 whitespace-pre-wrap break-words"
            v-html="getTextPreview(newsItem)"
          />
        </div>
      </div>
    </div>
    <CardActionsPanel
      :name="newsItem.name"
      :comments="commentsCounter"
      :reactions="reactionsCounters"
      :page-link="newsPageLink"
      :route="route"
      :is-counters-processing="isCountersProcessing"
      @change-reaction="onChangeReaction"
    >
      <FavoritesButton
        v-if="authStore.auth"
        :entity-title="getShortName(newsItem.name)"
        :entity-id="newsItem.id"
        :initial-value="favoriteInitialValue ?? isFavorite"
        :entity-type="EntityType.Post"
      />
    </CardActionsPanel>
  </article>
</template>

<script setup lang="ts">
import { useNews } from '~/stores/news'
import type { NewsListItem } from '~/models/news'
import { EntityType } from '~/models/common'
import { useNavigation } from '~/stores/navigation'
import { useAuth } from '~/stores/auth'

const authStore = useAuth()

const props = defineProps({
  newsItem: {
    type: Object as PropType<NewsListItem>,
    required: true,
  },
  favoriteInitialValue: {
    type: Boolean,
    default: undefined,
  },
})

const { formatDateFrom, getTextPreview, getNewsPageLink, getShortName } =
  useNews()
const { getCategoryItemsByIds, getPageLinkCategorySlug } = useNavigation()

const categories = getCategoryItemsByIds(props.newsItem.categories, 'news')
const categorySlug = getPageLinkCategorySlug(categories)

const route = {
  name: 'news-category-item-id',
  params: {
    id: props.newsItem.id,
    category: categorySlug,
  },
}

const newsPageLink = getNewsPageLink(props.newsItem.id, categorySlug)

const entity = {
  entity_id: props.newsItem.id,
  entity_type: EntityType.Post,
}

const target = ref(null)
const {
  initCountersHelper,
  commentsCounter,
  reactionsCounters,
  onChangeReaction,
  isCountersProcessing,
  isFavorite,
} = useCountersHelper()

onMounted(async () => {
  await initCountersHelper({
    entity,
    target,
    isLoadComments: true,
    isLoadReactions: true,
    isCheckFavorite: authStore.auth
      ? props.favoriteInitialValue === undefined
      : false,
  })
})
</script>
